/**
 * <card-colors> - farvetaelleren "+N" i produktkortets meta-baand, KUN mobil
 * (< 834px). Folder kortets EGEN variantstribe ud hen over fotoets bund -
 * desktops hover-reveal, oversat til et tryk (ejerbeslutning 2026-09-29).
 *
 * Progressive enhancement: serveren renderer taelleren som et <a> til PDP'en,
 * hvor app-blokken viser hele farvelisten. Uden JS er det stadig et rigtigt,
 * virkende link. Med JS byttes elementet til en <button> med aria-expanded,
 * og striben aabnes paa stedet. Klassen klaeder begge elementer
 * (assets/product-card.css), saa swappet er usynligt.
 *
 * Glyffen foelger PDP-accordionen (blocks/_disclosure-row.liquid): '+' bliver
 * '-' (U+2212) naar raekken er aaben. Ingen ny motion - striben selv toner ind
 * paa --dur, og tekstskiftet sker i ét trin.
 *
 * Kun ÉT kort ad gangen: et aabent kort daekker nabokortets foto i griddet, og
 * to aabne striber er praecis det rod taelleren skal undgaa. Lytterne ligger
 * paa modul-niveau og registreres derfor én gang, ikke én gang pr. kort - en
 * PLP har 24+ instanser.
 *
 * Opgraderingen koerer ved ALLE bredder, ogsaa over 834px hvor taelleren er
 * display:none. Det er med vilje: en matchMedia-gate ville skulle koere igen
 * ved hver resize og ved hver Section Rendering-genrender for at ramme en
 * bruger der roterer sin tablet, og prisen er 24 element-swaps paa en PLP.
 * Konsekvensen er en skjult, uoperabel <button> i desktop-DOM'en - den er
 * display:none, saa den er hverken i tab-ordenen eller i AT-traeet.
 */
const OPEN_CLASS = 'product-card--colors-open';

/** Den ene aabne instans, eller null. */
let openInstance = null;
let uid = 0;

function closeOpen() {
  if (openInstance) openInstance.close();
}

// Tryk uden for det aabne kort lukker. `pointerdown` og ikke `click`: paa
// touch skal striben vaere vaek FOER fingeren loeftes, ellers naar brugeren at
// se den lukke oven paa det den trykkede paa. Tryk inde i kortet (taelleren
// selv, eller en celle der navigerer) haandteres af kortet.
document.addEventListener('pointerdown', (event) => {
  if (!openInstance) return;
  const el = event.target instanceof Element ? event.target : null;
  if (el && openInstance.card && openInstance.card.contains(el)) return;
  closeOpen();
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || !openInstance) return;
  // En aaben <dialog> ejer sin egen Escape (samme vagt som
  // collection-filters.js). Uden den lukker ét tryk baade filter-modalen og
  // et kort bagved, og de to handlers slaas om fokus.
  if (document.querySelector('dialog[open]')) return;
  // Fokus foeres KUN tilbage naar det staar i kortet - altsaa paa taelleren
  // eller paa en celle der lige er blevet visibility:hidden. Stod fokus et
  // helt andet sted paa siden, ville et Escape ellers rive det op til et kort
  // brugeren for laengst har forladt.
  const { card, trigger } = openInstance;
  const returnFocus = card && card.contains(document.activeElement);
  closeOpen();
  if (returnFocus && trigger) trigger.focus();
});

// Section Rendering og theme editoren skifter hele griddet ud under os.
// Den aabne instans forsvinder da uden disconnectedCallback i aeldre Safari,
// saa referencen ryddes ogsaa her.
document.addEventListener('shopify:section:load', closeOpen);
document.addEventListener('shopify:section:unload', closeOpen);

class CardColors extends HTMLElement {
  connectedCallback() {
    // Allerede opgraderet (elementet er flyttet frem for gen-renderet): lad
    // vaere at bytte noget om igen, saa instansen ikke ender halvt opdateret
    // med referencer til to forskellige kort.
    if (this.trigger) return;

    const link = this.querySelector('a');
    const card = this.closest('.product-card');
    const strip = card && card.querySelector('.product-card__variants');
    // Ingen stribe at folde ud: lad linket vaere et link til PDP'en.
    if (!link || !strip) return;

    this.card = card;
    this.strip = strip;
    // Kun fortegnet skiftes naar striben aabner - taellerens tal er et
    // tekstnode-soeskende. Fortegnet har sin egen span fordi det loeftes
    // optisk i CSS (Helvetica Neues plus staar 1,3px for lavt), og et
    // textContent-skift paa hele taelleren ville rive spanen ud.
    this.sign = link.querySelector('.product-card__colors-sign');

    // <a> -> <button>. Attributter og boern flyttes med, saa det
    // tilgaengelige navn (den visually-hidden tekst) overlever.
    const button = document.createElement('button');
    button.type = 'button';
    button.className = link.className;
    while (link.firstChild) button.appendChild(link.firstChild);
    if (!strip.id) {
      uid += 1;
      strip.id = `product-card-colors-${uid}`;
    }
    button.setAttribute('aria-controls', strip.id);
    button.setAttribute('aria-expanded', 'false');
    link.replaceWith(button);
    this.trigger = button;

    button.addEventListener('click', (event) => {
      if (openInstance === this) {
        this.close();
        return;
      }
      closeOpen();
      this.open();
      // Striben ligger FOER taelleren i DOM'en (den bor i meta-baandet, over
      // titlen), saa de afsloerede celler ligger bag brugeren i tab-ordenen.
      // En tastaturbruger foeres derfor ind i striben; Escape sender fokus
      // tilbage til taelleren, saa loekken lukker. `detail === 0` betyder
      // tastatur-aktivering - ved tryk og klik bliver fokus paa taelleren,
      // saa der ikke tegnes en fokusring paa det foerste thumbnail.
      if (event.detail === 0) {
        const first = this.strip.querySelector('.product-card__variant');
        if (first) first.focus();
      }
    });
  }

  disconnectedCallback() {
    if (openInstance === this) openInstance = null;
  }

  open() {
    this.card.classList.add(OPEN_CLASS);
    this.trigger.setAttribute('aria-expanded', 'true');
    this.#glyph('−');
    // Striben er en scroller: et genaabnet kort skal starte forfra.
    this.strip.scrollLeft = 0;
    openInstance = this;
  }

  close() {
    this.card.classList.remove(OPEN_CLASS);
    this.trigger.setAttribute('aria-expanded', 'false');
    this.#glyph('+');
    if (openInstance === this) openInstance = null;
  }

  #glyph(sign) {
    if (this.sign) this.sign.textContent = sign;
  }
}

if (!customElements.get('card-colors')) {
  customElements.define('card-colors', CardColors);
}
